import { detectSourceFormat } from "./client-convert";

// The file formats the app handles, and the rules mapping one to the other.
// Both converters (single-file and batch) read this module rather than keeping
// their own copies — a picker that offered a target the conversion loop didn't
// agree with is what silently turned FIT output into GPX.
//
// Nothing here imports the FIT SDK at runtime, so it stays out of the initial
// bundle; see the header of ./client-convert.

// Every output format convertFile can produce. Also the source of the
// ConversionTarget union and the array both format pickers render, so a target
// can't exist in the UI without existing here.
export const CONVERSION_TARGETS = ["fit", "gpx", "tcx", "csv"] as const;

export type ConversionTarget = (typeof CONVERSION_TARGETS)[number];

const SUPPORTED_INPUT = /\.(fit|tcx|xml)$/i;

export function isSupportedInput(file: File): boolean {
  return SUPPORTED_INPUT.test(file.name);
}

// Every supported input converts to every target — same-format output included,
// since it applies edits (trim, HR fix, field strip) without converting. The
// per-file list exists only to rule out inputs we can't read at all.
export function getAvailableTargets(file: File): readonly ConversionTarget[] {
  return isSupportedInput(file) ? CONVERSION_TARGETS : [];
}

// The target pre-selected on upload — a different format from the source,
// since converting is the common case and same-format output is the edit-only
// path the user has to opt into.
export function inferTarget(file: File): ConversionTarget {
  return detectSourceFormat(file) === "fit" ? "gpx" : "fit";
}

export function getDownloadName(file: File, target: ConversionTarget): string {
  const base = file.name.replace(/\.(tcx|xml|fit)$/i, "") || "workout";
  return `${base}.${target}`;
}

// react-dropzone `accept` config, shared so the file picker can't drift from
// isSupportedInput.
export const ACCEPTED_INPUT_TYPES = {
  "application/xml": [".tcx", ".xml"],
  "text/xml": [".tcx", ".xml"],
  "application/octet-stream": [".fit"],
};
